import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Form,
  Button,
  Alert,
  Row,
  Col,
  Tabs,
  Tab,
  Spinner,
  Badge,
  Card,
  ListGroup,
} from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import api from '../api';

const MAX_IMAGES = 12;

const emptyFormData = {
  sku: '',
  name: '',
  price: '',
  map_price: '',
  stock: '',
  description: '',
  images: [],
  binLocation: '',
  length: '',
  width: '',
  height: '',
  weight: '',
  listedOn: [],
  ebay_category_id: '',
  ebay_category_label: '',
  ebay_category_path: '',
  ebay_category_confidence: '',
  ebay_category_search_terms: [],
  ebay_specifics: {},
  ebay_required_specifics_missing: [],
  condition: '',
  condition_notes: '',
  brand: '',
  model: '',
  upc: '',
  ean: '',
  isbn: '',
  gtin: '',
  mpn: '',
};

const AddProduct = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(emptyFormData);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [specificFields, setSpecificFields] = useState([]);
  const [selectedEbayCategory, setSelectedEbayCategory] = useState(null);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [aiWarning, setAiWarning] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [ebayDraftResult, setEbayDraftResult] = useState(null);
  const [aiStepMessage, setAiStepMessage] = useState('');
  const [aiAutoCategoryNote, setAiAutoCategoryNote] = useState(null);

  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingSpecifics, setIsLoadingSpecifics] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [isRefillingSpecifics, setIsRefillingSpecifics] = useState(false);
  const [isCreatingEbayDraft, setIsCreatingEbayDraft] = useState(false);
  const [isSubmittingProduct, setIsSubmittingProduct] = useState(false);

  useEffect(() => {
    return () => {
      imagePreviews.forEach((preview) => {
        if (preview.url) URL.revokeObjectURL(preview.url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const normalizeArray = (value) => {
    if (!value) return [];
    return Array.isArray(value) ? value.filter(Boolean) : [value].filter(Boolean);
  };

  const normalizeComparable = (value) =>
    cleanText(value)
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '');

  const normalizeSpecificKey = (key) => {
    const raw = cleanText(key);
    const lower = raw.toLowerCase();

    const aliases = {
      brand: 'Brand',
      manufacturer: 'Brand',
      model: 'Model',
      'model number': 'Model',
      model_number: 'Model',
      mpn: 'MPN',
      'manufacturer part number': 'MPN',
      upc: 'UPC',
      ean: 'EAN',
      isbn: 'ISBN',
      gtin: 'GTIN',
      type: 'Type',
      color: 'Color',
      size: 'Size',
      material: 'Material',
      department: 'Department',
      style: 'Style',
      features: 'Features',
      'country/region of manufacture': 'Country/Region of Manufacture',
      'country of manufacture': 'Country/Region of Manufacture',
      'country of origin': 'Country of Origin',
      'item length': 'Item Length',
      'item width': 'Item Width',
      'item height': 'Item Height',
      'item weight': 'Item Weight',
      'compatible brand': 'Compatible Brand',
      'compatible model': 'Compatible Model',
      platform: 'Platform',
      game: 'Game',
      character: 'Character',
      franchise: 'Franchise',
      set: 'Set',
      'card name': 'Card Name',
      'card number': 'Card Number',
      rarity: 'Rarity',
      finish: 'Finish',
      language: 'Language',
      'age level': 'Age Level',
      'recommended age range': 'Recommended Age Range',
      year: 'Year',
      'release year': 'Year',
      'character family': 'Character Family',
      'california prop 65 warning': 'California Prop 65 Warning',
      'original/licensed reprint': 'Original/Licensed Reprint',
      graded: 'Graded',
      'card condition': 'Card Condition',
      'unit quantity': 'Unit Quantity',
      'unit type': 'Unit Type',
    };

    return aliases[lower] || raw;
  };

  const normalizeSpecificValue = (value) => {
    if (value === null || value === undefined || value === '') return '';

    if (Array.isArray(value)) {
      return value.map((item) => cleanText(item)).filter(Boolean).join(', ');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return cleanText(value);
  };

  const normalizeSpecificsObject = (specifics = {}) => {
    const output = {};

    if (!specifics || typeof specifics !== 'object' || Array.isArray(specifics)) {
      return output;
    }

    Object.entries(specifics).forEach(([key, value]) => {
      const cleanKey = normalizeSpecificKey(key);
      const cleanValue = normalizeSpecificValue(value);

      if (!cleanKey || !cleanValue) return;
      if (['null', 'unknown', 'n/a'].includes(cleanValue.toLowerCase())) return;

      output[cleanKey] = cleanValue;
    });

    return output;
  };

  const findSpecificValue = (specifics, names = []) => {
    const normalized = normalizeSpecificsObject(specifics);

    for (const name of names) {
      const exact = normalized[name];
      if (exact) return exact;

      const normalizedName = normalizeSpecificKey(name);
      if (normalized[normalizedName]) return normalized[normalizedName];

      const foundKey = Object.keys(normalized).find(
        (key) => normalizeComparable(key) === normalizeComparable(name)
      );

      if (foundKey && normalized[foundKey]) return normalized[foundKey];
    }

    return '';
  };

  const getAllowedValuesForSpecificField = (field = {}) => {
    if (!field.values) return [];

    return normalizeArray(field.values)
      .map((value) => {
        if (typeof value === 'string') return value;
        return value.localizedValue || value.value || value.name || '';
      })
      .map(cleanText)
      .filter(Boolean);
  };

  const pickAllowedSpecificValue = (value, field = {}) => {
    const cleaned = cleanText(value);
    if (!cleaned) return '';

    const allowedValues = getAllowedValuesForSpecificField(field);
    if (!allowedValues.length || field.isFreeText) return cleaned;

    const cleanedComparable = normalizeComparable(cleaned);

    const exact = allowedValues.find(
      (allowed) => normalizeComparable(allowed) === cleanedComparable
    );
    if (exact) return exact;

    const loose = allowedValues.find((allowed) => {
      const allowedComparable = normalizeComparable(allowed);
      return (
        allowedComparable.includes(cleanedComparable) ||
        cleanedComparable.includes(allowedComparable)
      );
    });

    return loose || '';
  };

  const getTextBlobForGuessing = (data = formData) =>
    cleanText(
      [
        data.name,
        data.description,
        data.condition,
        data.condition_notes,
        data.brand,
        data.model,
        data.ebay_category_label,
        data.ebay_category_path,
        Object.values(data.ebay_specifics || {}).join(' '),
      ].join(' ')
    );

  const looksPokemon = (data = formData) => {
    const text = getTextBlobForGuessing(data).toLowerCase();
    return text.includes('pokemon') || text.includes('pokémon');
  };

  const looksTradingCard = (data = formData) => {
    const text = getTextBlobForGuessing(data).toLowerCase();
    return (
      looksPokemon(data) ||
      text.includes('trading card') ||
      text.includes('tcg') ||
      text.includes('card number') ||
      text.includes('foil') ||
      text.includes('holo')
    );
  };

  const looksSealed = (data = formData) => {
    const text = getTextBlobForGuessing(data).toLowerCase();
    return (
      text.includes('sealed') ||
      text.includes('unopened') ||
      text.includes('booster pack') ||
      text.includes('booster box') ||
      text.includes('elite trainer box') ||
      text.includes(' etb') ||
      text.includes('collection box') ||
      text.includes('bundle') ||
      text.includes('tin')
    );
  };

  const getKnownValueForSpecificField = (fieldName, data = formData, specifics = {}) => {
    const normalizedSpecifics = normalizeSpecificsObject(specifics || data.ebay_specifics || {});
    const normalizedName = normalizeSpecificKey(fieldName);
    const lowerName = normalizedName.toLowerCase();
    const text = getTextBlobForGuessing(data);
    const textLower = text.toLowerCase();

    const existing =
      normalizedSpecifics[fieldName] ||
      normalizedSpecifics[normalizedName] ||
      findSpecificValue(normalizedSpecifics, [fieldName, normalizedName]);

    if (existing) return existing;

    const directMap = {
      Brand: data.brand,
      Manufacturer: data.brand,
      Model: data.model,
      MPN: data.mpn,
      UPC: data.upc,
      EAN: data.ean,
      ISBN: data.isbn,
      GTIN: data.gtin,
      Condition: data.condition,
      'Item Length': data.length ? `${data.length} in` : '',
      'Item Width': data.width ? `${data.width} in` : '',
      'Item Height': data.height ? `${data.height} in` : '',
      'Item Weight': data.weight ? `${data.weight} lb` : '',
    };

    if (directMap[normalizedName]) return directMap[normalizedName];

    if (lowerName === 'type') {
      if (looksTradingCard(data)) return looksSealed(data) ? 'Sealed Product' : 'Sports Trading Card';
      return data.ebay_category_label || data.ebay_category_path || '';
    }

    if (lowerName === 'game' && looksPokemon(data)) return 'Pokémon TCG';
    if (lowerName === 'franchise' && looksPokemon(data)) return 'Pokémon';

    if (lowerName === 'manufacturer') {
      if (looksPokemon(data)) return 'The Pokémon Company';
      if (data.brand) return data.brand;
    }

    if (lowerName === 'language') {
      if (textLower.includes('japanese')) return 'Japanese';
      return 'English';
    }

    if (lowerName === 'graded') {
      if (textLower.includes('psa') || textLower.includes('cgc') || textLower.includes('beckett')) {
        return 'Yes';
      }
      if (looksTradingCard(data)) return 'No';
    }

    if (lowerName === 'card condition') {
      if (data.condition && data.condition.toLowerCase().includes('new')) return 'Near Mint or Better';
      if (data.condition && data.condition.toLowerCase().includes('damaged')) return 'Poor';
      if (looksTradingCard(data)) return 'Near Mint or Better';
    }

    if (lowerName === 'original/licensed reprint') {
      if (looksTradingCard(data) || looksPokemon(data)) return 'Original';
    }

    if (lowerName === 'country/region of manufacture') {
      const found = findSpecificValue(normalizedSpecifics, [
        'Country/Region of Manufacture',
        'Country of Origin',
      ]);
      if (found) return found;
    }

    if (lowerName === 'california prop 65 warning') {
      return 'No warning applicable';
    }

    return '';
  };

  const autoFillSpecificsFromKnownData = (
    data = formData,
    fields = specificFields,
    baseSpecifics = data.ebay_specifics || {}
  ) => {
    const normalizedBase = normalizeSpecificsObject(baseSpecifics);
    const output = { ...normalizedBase };

    if (!fields || !fields.length) return output;

    fields.forEach((field) => {
      const fieldName = cleanText(field.name);
      if (!fieldName) return;

      const current =
        output[fieldName] ||
        output[normalizeSpecificKey(fieldName)] ||
        findSpecificValue(output, [fieldName]);

      if (current) {
        const pickedCurrent = pickAllowedSpecificValue(current, field);
        output[fieldName] = pickedCurrent || current;
        return;
      }

      const knownValue = getKnownValueForSpecificField(fieldName, data, output);
      const pickedValue = pickAllowedSpecificValue(knownValue, field);

      if (pickedValue) {
        output[fieldName] = pickedValue;
      }
    });

    return output;
  };

  const mergeSpecificsForCurrentCategory = (currentSpecifics, aiSpecifics, fields) => {
    const normalizedCurrent = normalizeSpecificsObject(currentSpecifics || {});
    const normalizedAi = normalizeSpecificsObject(aiSpecifics || {});
    const output = {};

    if (!fields || !fields.length) {
      return {
        ...normalizedCurrent,
        ...normalizedAi,
      };
    }

    const allowedNames = new Set(fields.map((field) => normalizeSpecificKey(field.name)));

    fields.forEach((field) => {
      const fieldName = normalizeSpecificKey(field.name);

      const aiValue =
        normalizedAi[fieldName] ||
        normalizedAi[field.name] ||
        findSpecificValue(normalizedAi, [fieldName, field.name]);

      const currentValue =
        normalizedCurrent[fieldName] ||
        normalizedCurrent[field.name] ||
        findSpecificValue(normalizedCurrent, [fieldName, field.name]);

      const rawValue = aiValue || currentValue;
      const pickedValue = pickAllowedSpecificValue(rawValue, field);

      if (pickedValue) output[field.name] = pickedValue;
      else if (rawValue && field.isFreeText) output[field.name] = rawValue;
    });

    Object.entries(normalizedAi).forEach(([key, value]) => {
      const normalizedKey = normalizeSpecificKey(key);

      if (allowedNames.has(normalizedKey)) {
        const exactField = fields.find(
          (field) => normalizeSpecificKey(field.name) === normalizedKey
        );
        const pickedValue = pickAllowedSpecificValue(value, exactField);

        if (pickedValue || exactField?.isFreeText) {
          output[exactField?.name || key] = pickedValue || value;
        }
      }
    });

    return output;
  };

  const applyCommonFieldsFromAI = (ai, aiSpecifics = {}, currentData = formData) => {
    const normalized = normalizeSpecificsObject(aiSpecifics);

    return {
      brand: ai.brand || findSpecificValue(normalized, ['Brand', 'Manufacturer']) || currentData.brand,
      model: ai.model || findSpecificValue(normalized, ['Model']) || currentData.model,
      upc: ai.upc || findSpecificValue(normalized, ['UPC']) || currentData.upc,
      ean: ai.ean || findSpecificValue(normalized, ['EAN']) || currentData.ean,
      isbn: ai.isbn || findSpecificValue(normalized, ['ISBN']) || currentData.isbn,
      gtin: ai.gtin || findSpecificValue(normalized, ['GTIN']) || currentData.gtin,
      mpn: ai.mpn || findSpecificValue(normalized, ['MPN']) || currentData.mpn,
    };
  };

  const getBestAIPrice = () => {
   const possiblePrices = [
    aiResult?.suggested_price,
    aiResult?.price,
    aiResult?.optimized_price,
    aiResult?.recommended_price,

    aiResult?.listing?.suggested_price,
    aiResult?.listing?.price,
    aiResult?.listing?.optimized_price,
    aiResult?.listing?.recommended_price,

    aiResult?.pricing?.suggested_price,
    aiResult?.pricing?.price,

    aiResult?.pricing_result?.suggested_price,
    aiResult?.pricing_result?.price,

    aiResult?.profit_preview?.estimated_sale_price,
    aiResult?.listing?.profit_preview?.estimated_sale_price,
  ];

  for (const value of possiblePrices) {
    const priceNumber = Number(value);

    if (Number.isFinite(priceNumber) && priceNumber > 0) {
      return priceNumber.toFixed(2);
    }
  }

  return '';
};

const getBestPriceFromAIResponse = (ai, fallbackPrice = '') => {
  const possiblePrices = [
    ai?.suggested_price,
    ai?.price,
    ai?.optimized_price,
    ai?.recommended_price,

    ai?.listing?.suggested_price,
    ai?.listing?.price,
    ai?.listing?.optimized_price,
    ai?.listing?.recommended_price,

    ai?.pricing?.suggested_price,
    ai?.pricing?.price,

    ai?.pricing_result?.suggested_price,
    ai?.pricing_result?.price,

    ai?.profit_preview?.estimated_sale_price,
    ai?.listing?.profit_preview?.estimated_sale_price,
  ];

  for (const value of possiblePrices) {
    const priceNumber = Number(value);

    if (Number.isFinite(priceNumber) && priceNumber > 0) {
      return priceNumber.toFixed(2);
    }
  }

  return cleanText(fallbackPrice);
};

  const getBestAIName = () => {
    return (
      cleanText(aiResult?.title) ||
      cleanText(aiResult?.name) ||
      cleanText(aiResult?.listing?.title) ||
      cleanText(aiResult?.listing?.name) ||
      ''
    );
  };

  const buildRequiredSpecificsForAI = (fields = specificFields) => {
    return fields.map((field) => ({
      name: field.name,
      required: Boolean(field.required),
      recommendedValues: normalizeArray(field.values),
    }));
  };

  const buildCandidateCategoriesForAI = (categoryOverride = null) => {
    const categories = [];

    if (categoryOverride) {
      categories.push({
        id: categoryOverride.value || categoryOverride.id || categoryOverride.categoryId || '',
        value: categoryOverride.value || categoryOverride.id || categoryOverride.categoryId || '',
        categoryId: categoryOverride.value || categoryOverride.id || categoryOverride.categoryId || '',
        name: categoryOverride.rawName || categoryOverride.name || categoryOverride.label || '',
        rawName: categoryOverride.rawName || categoryOverride.name || categoryOverride.label || '',
        path: categoryOverride.path || categoryOverride.label || categoryOverride.categoryPath || '',
        categoryPath: categoryOverride.path || categoryOverride.label || categoryOverride.categoryPath || '',
        label: categoryOverride.path || categoryOverride.label || categoryOverride.rawName || '',
      });
    }

    if (selectedEbayCategory) {
      categories.push({
        id: selectedEbayCategory.value,
        value: selectedEbayCategory.value,
        categoryId: selectedEbayCategory.value,
        name: selectedEbayCategory.rawName || selectedEbayCategory.label,
        rawName: selectedEbayCategory.rawName || selectedEbayCategory.label,
        path: selectedEbayCategory.path || selectedEbayCategory.label,
        categoryPath: selectedEbayCategory.path || selectedEbayCategory.label,
        label: selectedEbayCategory.path || selectedEbayCategory.label,
      });
    }

    if (formData.ebay_category_id || formData.ebay_category_label) {
      categories.push({
        id: formData.ebay_category_id,
        value: formData.ebay_category_id,
        categoryId: formData.ebay_category_id,
        name: formData.ebay_category_label,
        rawName: formData.ebay_category_label,
        path: formData.ebay_category_path || formData.ebay_category_label,
        categoryPath: formData.ebay_category_path || formData.ebay_category_label,
        label: formData.ebay_category_path || formData.ebay_category_label,
      });
    }

    const seen = new Set();

    return categories.filter((cat) => {
      const key = `${cat.id || ''}-${cat.name || ''}-${cat.path || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return cat.id || cat.name || cat.path;
    });
  };

  const buildAIWarningMessage = (ai) => {
    const warnings = [];

    if (Array.isArray(ai.missing_info) && ai.missing_info.length) {
      warnings.push(`Missing info: ${ai.missing_info.join(', ')}`);
    }

    if (Array.isArray(ai.required_specifics_missing) && ai.required_specifics_missing.length) {
      warnings.push(`Missing eBay specifics: ${ai.required_specifics_missing.join(', ')}`);
    }

    if (Array.isArray(ai.warnings) && ai.warnings.length) {
      warnings.push(...ai.warnings);
    }

    if (ai.category_confidence !== null && ai.category_confidence !== undefined) {
      const pct = Math.round(Number(ai.category_confidence) * 100);
      if (!Number.isNaN(pct) && pct < 75) {
        warnings.push(`Category confidence is low (${pct}%).`);
      }
    }

    if (ai.needs_review) {
      warnings.push('AI marked this listing as needing review.');
    }

    return warnings.join(' ');
  };

  const createAIFormData = ({
    currentData = formData,
    categoryOption = null,
    requiredFields = specificFields,
    specificsOverride = null,
    categoryLocked = false,
    candidateCategoriesOverride = null,
    extraInstructionOverride = '',
  } = {}) => {
    const aiData = new FormData();

    currentData.images.forEach((file) => {
      aiData.append('images', file);
    });

    const categoryId =
      categoryOption?.value ||
      categoryOption?.id ||
      categoryOption?.categoryId ||
      currentData.ebay_category_id ||
      '';

    const categoryLabel =
      categoryOption?.rawName ||
      categoryOption?.name ||
      categoryOption?.label ||
      currentData.ebay_category_label ||
      '';

    const categoryPath =
      categoryOption?.path ||
      categoryOption?.categoryPath ||
      categoryOption?.label ||
      currentData.ebay_category_path ||
      '';

    const candidateCategories =
      candidateCategoriesOverride && candidateCategoriesOverride.length
        ? candidateCategoriesOverride
        : buildCandidateCategoriesForAI(
            categoryOption
              ? {
                  ...categoryOption,
                  value: categoryId,
                  label: categoryPath || categoryLabel,
                  rawName: categoryLabel,
                  path: categoryPath || categoryLabel,
                }
              : null
          );

    aiData.append('sku', currentData.sku || '');
    aiData.append('currentTitle', currentData.name || '');
    aiData.append('currentDescription', currentData.description || '');
    aiData.append('currentPrice', currentData.price || '');
    aiData.append('currentCategoryId', categoryId);
    aiData.append('currentCategoryLabel', categoryPath || categoryLabel);
    aiData.append(
      'currentSpecifics',
      JSON.stringify(specificsOverride || currentData.ebay_specifics || {})
    );
    aiData.append('candidateCategories', JSON.stringify(candidateCategories));
    aiData.append('requiredSpecifics', JSON.stringify(buildRequiredSpecificsForAI(requiredFields)));
    aiData.append('conditionHint', currentData.condition || '');
    aiData.append('includedAccessoriesHint', '');

    if (categoryLocked) {
      aiData.append('categoryLocked', 'true');
      aiData.append(
        'extraInstruction',
        extraInstructionOverride ||
          `The eBay category is already selected. Do not change the category. Fill only the item specifics that match this selected category: ${categoryPath || categoryLabel} (${categoryId}).`
      );
    } else if (extraInstructionOverride) {
      aiData.append('extraInstruction', extraInstructionOverride);
    }

    return aiData;
  };

  const generateSkuIfNeeded = async (currentSku) => {
    const existingSku = cleanText(currentSku);
    if (existingSku) return existingSku;

    try {
      const response = await api.get('/tools/sku');
      const generatedSku = cleanText(response.data?.sku);

      if (!generatedSku) {
        throw new Error('SKU generator did not return a SKU.');
      }

      setFormData((prev) => ({
        ...prev,
        sku: generatedSku,
      }));

      return generatedSku;
    } catch (err) {
      console.error('[AddProduct] Failed to auto-generate SKU:', err);
      throw new Error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          'Failed to auto-generate SKU.'
      );
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setEbayDraftResult(null);
  };

  const handleFileChange = (e) => {
    setError(null);
    setAiWarning(null);
    setEbayDraftResult(null);
    setAiAutoCategoryNote(null);

    const selectedFiles = Array.from(e.target.files || []);

    if (!selectedFiles.length) {
      imagePreviews.forEach((preview) => {
        if (preview.url) URL.revokeObjectURL(preview.url);
      });

      setFormData((prev) => ({ ...prev, images: [] }));
      setImagePreviews([]);
      return;
    }

    const imageFiles = selectedFiles.filter((file) => String(file.type || '').startsWith('image/'));

    if (imageFiles.length !== selectedFiles.length) {
      setError('Only image files are allowed.');
      return;
    }

    if (imageFiles.length > MAX_IMAGES) {
      setError(`You can upload a maximum of ${MAX_IMAGES} images.`);
      return;
    }

    imagePreviews.forEach((preview) => {
      if (preview.url) URL.revokeObjectURL(preview.url);
    });

    const previews = imageFiles.map((file, index) => ({
      id: `${file.name}-${file.size}-${index}`,
      name: file.name,
      url: URL.createObjectURL(file),
      size: file.size,
    }));

    setFormData((prev) => ({ ...prev, images: imageFiles }));
    setImagePreviews(previews);
  };

  const removeImage = (indexToRemove) => {
    setError(null);
    setAiWarning(null);
    setEbayDraftResult(null);

    setImagePreviews((prev) => {
      const removed = prev[indexToRemove];
      if (removed?.url) URL.revokeObjectURL(removed.url);

      return prev.filter((_, index) => index !== indexToRemove);
    });

    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleListOnPlatform = (platform) => {
    setFormData((prev) => {
      const alreadySelected = prev.listedOn.includes(platform);

      const nextListedOn = alreadySelected
        ? prev.listedOn.filter((p) => p !== platform)
        : [...prev.listedOn, platform];

      const nextData = {
        ...prev,
        listedOn: nextListedOn,
      };

      if (platform === 'eBay' && alreadySelected) {
        nextData.ebay_category_id = '';
        nextData.ebay_category_label = '';
        nextData.ebay_category_path = '';
        nextData.ebay_category_confidence = '';
        nextData.ebay_category_search_terms = [];
        nextData.ebay_specifics = {};
        nextData.ebay_required_specifics_missing = [];
        setSpecificFields([]);
        setSelectedEbayCategory(null);
        setEbayDraftResult(null);
        setAiAutoCategoryNote(null);
      }

      return nextData;
    });
  };

  const loadCategories = useCallback(async (inputValue) => {
    setError(null);
    setIsLoadingCategories(true);

    try {
      if (!inputValue.trim() || inputValue.length < 2) {
        return [];
      }

      const response = await api.get('/ebay/categories', {
        params: { search: inputValue },
      });

      const categories = response.data.categories
        ? response.data.categories.map((cat) => {
            const categoryPath = cat.categoryPath || cat.fullPath || cat.categoryName || '';

            const label =
              categoryPath && categoryPath !== cat.categoryName
                ? `${categoryPath} (ID: ${cat.categoryId})`
                : cat.categoryName
                  ? `${cat.categoryName} (ID: ${cat.categoryId})`
                  : `ID: ${cat.categoryId}`;

            return {
              value: cat.categoryId,
              label,
              rawName: cat.categoryName || '',
              path: categoryPath,
            };
          })
        : [];

      return categories;
    } catch (err) {
      console.error('Error fetching eBay categories:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });

      let errorMessage = 'Failed to load eBay categories';

      if (err.response?.status === 401) {
        errorMessage = 'eBay authorization required. Redirecting to authenticate...';
        setTimeout(() => (window.location.href = '/auth/ebay'), 2000);
      } else if (err.response?.status === 429) {
        errorMessage = 'eBay API rate limit exceeded. Please try again later.';
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid search query for eBay categories';
      } else if (err.response?.status === 404) {
        errorMessage = 'No categories found for this search term';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. Please try again.';
      }

      setError(errorMessage);
      return [];
    } finally {
      setIsLoadingCategories(false);
    }
  }, []);

  const fetchCategorySpecifics = async (categoryId, existingSpecifics = {}, dataOverride = null) => {
    setIsLoadingSpecifics(true);

    try {
      const response = await api.get('/ebay/category-specifics', {
        params: { categoryId },
      });

      const specifics = response.data.specifics || [];
      setSpecificFields(specifics);

      if (!specifics.length) {
        setError('No specifics available for this category. You can still proceed.');
      }

      setFormData((prev) => {
        const baseData = dataOverride || prev;

        const normalizedExisting =
          existingSpecifics && Object.keys(existingSpecifics).length > 0
            ? normalizeSpecificsObject(existingSpecifics)
            : {};

        const mergedSpecifics = {
          ...(baseData.ebay_specifics || {}),
          ...normalizedExisting,
        };

        return {
          ...prev,
          ebay_specifics: autoFillSpecificsFromKnownData(baseData, specifics, mergedSpecifics),
        };
      });

      return specifics;
    } catch (err) {
      console.error('Error fetching category specifics:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });

      let errorMessage = 'Failed to load eBay category specifics';

      if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Redirecting to login...';
        setTimeout(() => (window.location.href = '/login'), 2000);
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid eBay category selected';
      } else if (err.response?.status === 404) {
        errorMessage = 'No specifics available for this category. You can still proceed.';
      } else if (err.response?.status === 429) {
        errorMessage = 'eBay API rate limit exceeded. Please try again later.';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out while fetching specifics. Please try again.';
      }

      setError(errorMessage);
      return [];
    } finally {
      setIsLoadingSpecifics(false);
    }
  };

  const refillSpecificsWithAI = async ({
    categoryOption = selectedEbayCategory,
    fields = specificFields,
    automatic = false,
    currentDataOverride = null,
  } = {}) => {
    setError(null);
    setEbayDraftResult(null);

    const currentData = currentDataOverride || formData;

    if (!currentData.images.length) {
      if (!automatic) setError('Upload images before filling eBay specifics with AI.');
      return null;
    }

    const categoryId =
      categoryOption?.value ||
      categoryOption?.id ||
      categoryOption?.categoryId ||
      currentData.ebay_category_id ||
      '';

    if (!categoryId) {
      if (!automatic) setError('Pick an eBay category before filling specifics with AI.');
      return null;
    }

    if (!fields.length) {
      if (!automatic) setError('No eBay specifics loaded for this category yet.');
      return null;
    }

    setIsRefillingSpecifics(true);
    setAiStepMessage('Filling eBay item specifics from the selected category...');

    try {
      const response = await api.post(
        '/ai/ebay-listing/generate',
        createAIFormData({
          currentData,
          categoryOption,
          requiredFields: fields,
          specificsOverride: currentData.ebay_specifics || {},
          categoryLocked: true,
        }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const ai = response.data?.listing || response.data || {};
      const aiSpecifics = normalizeSpecificsObject(ai.item_specifics || ai.ebay_specifics || {});

      const categorySpecifics = mergeSpecificsForCurrentCategory(
        currentData.ebay_specifics || {},
        aiSpecifics,
        fields
      );

      const commonFields = applyCommonFieldsFromAI(ai, aiSpecifics, currentData);

      const nextDataBase = {
        ...currentData,
        condition: ai.condition || currentData.condition,
        condition_notes: ai.condition_notes || ai.conditionNotes || currentData.condition_notes,
        brand: commonFields.brand || currentData.brand,
        model: commonFields.model || currentData.model,
        upc: commonFields.upc || currentData.upc,
        ean: commonFields.ean || currentData.ean,
        isbn: commonFields.isbn || currentData.isbn,
        gtin: commonFields.gtin || currentData.gtin,
        mpn: commonFields.mpn || currentData.mpn,
        ebay_specifics: categorySpecifics,
      };

      const finalSpecifics = autoFillSpecificsFromKnownData(
        nextDataBase,
        fields,
        categorySpecifics
      );

      const finalData = {
        ...nextDataBase,
        ebay_specifics: finalSpecifics,
        ebay_required_specifics_missing: normalizeArray(ai.required_specifics_missing),
      };

        setFormData((prev) => ({
        ...prev,
        price: prev.price || getBestPriceFromAIResponse(ai, currentData.price),
        condition: finalData.condition,
        condition_notes: finalData.condition_notes,
        brand: finalData.brand,
        model: finalData.model,
        upc: finalData.upc,
        ean: finalData.ean,
        isbn: finalData.isbn,
        gtin: finalData.gtin,
        mpn: finalData.mpn,
        ebay_specifics: finalData.ebay_specifics,
        ebay_required_specifics_missing: finalData.ebay_required_specifics_missing,
      }));

      setAiResult((prev) => ({
        ...(prev || {}),
        ...ai,
        category_refill_done: true,
      }));

      const warningMessage = buildAIWarningMessage(ai);

      if (warningMessage) {
        setAiWarning(warningMessage);
      } else if (!automatic) {
        setSuccess('AI filled the eBay specifics for the selected category. Review before submitting.');
      }

      return {
        success: true,
        ai,
        specifics: finalSpecifics,
        finalData,
      };
    } catch (err) {
      console.error('Error refilling eBay specifics with AI:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });

      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to fill eBay specifics with AI.';

      if (!automatic) setError(message);

      return {
        success: false,
        error: message,
      };
    } finally {
      setIsRefillingSpecifics(false);
      setAiStepMessage('');
    }
  };

  const handleCategoryChange = async (selectedOption) => {
    const categoryId = selectedOption ? selectedOption.value : '';
    const categoryLabel = selectedOption ? selectedOption.rawName || selectedOption.label : '';
    const categoryPath = selectedOption ? selectedOption.path || selectedOption.label : '';

    setSelectedEbayCategory(selectedOption || null);

    const nextData = {
      ...formData,
      ebay_category_id: categoryId,
      ebay_category_label: categoryLabel,
      ebay_category_path: categoryPath,
      ebay_category_confidence: '',
      ebay_required_specifics_missing: [],
      ebay_specifics: {},
    };

    setFormData(nextData);
    setSpecificFields([]);
    setError(null);
    setEbayDraftResult(null);

    if (categoryId) {
      const loadedSpecifics = await fetchCategorySpecifics(categoryId, {}, nextData);

      if (selectedOption && loadedSpecifics.length && nextData.images.length) {
        await refillSpecificsWithAI({
          categoryOption: selectedOption,
          fields: loadedSpecifics,
          automatic: true,
          currentDataOverride: nextData,
        });
      }
    }
  };

  const handleSpecificChange = (name) => (e) => {
    const value = e.target.value;

    setFormData((prev) => ({
      ...prev,
      ebay_specifics: {
        ...prev.ebay_specifics,
        [name]: value,
      },
    }));

    setEbayDraftResult(null);
  };

  const maybeAutoSelectCategoryFromAISearchTerms = async (ai, aiBaseData) => {
    const existingCategoryId =
      ai.ebay_category_id || ai.category_id || ai.categoryId || aiBaseData.ebay_category_id || '';

    if (existingCategoryId) return null;

    const terms = normalizeArray(ai.category_search_terms);
    const fallbackTerms = [
      ai.category_name,
      ai.category_path,
      ai.ebay_category_label,
      ai.title,
      ai.name,
      aiBaseData.name,
      aiBaseData.description,
    ].filter(Boolean);

    const searchTerms = [...terms, ...fallbackTerms]
      .map((term) => cleanText(term))
      .filter((term) => term.length >= 2)
      .slice(0, 5);

    let bestOptions = [];
    let bestTerm = '';

    for (const term of searchTerms) {
      try {
        setAiStepMessage(`Searching eBay categories for "${term}"...`);

        const options = await loadCategories(term);

        if (options && options.length === 1) {
          setAiAutoCategoryNote(`Auto-selected the only matching eBay category for "${term}".`);
          return options[0];
        }

        if (options && options.length > bestOptions.length) {
          bestOptions = options.slice(0, 25);
          bestTerm = term;
        }

        if (options && options.length >= 5) break;
      } catch (err) {
        console.warn('AI category auto-search failed:', term, err.message);
      }
    }

    if (!bestOptions.length) return null;

    try {
      setAiStepMessage('Asking AI to choose the best eBay category from real eBay matches...');

      const candidateCategories = bestOptions.map((option) => ({
        id: option.value,
        value: option.value,
        categoryId: option.value,
        name: option.rawName || option.label || '',
        rawName: option.rawName || option.label || '',
        path: option.path || option.label || '',
        categoryPath: option.path || option.label || '',
        label: option.path || option.label || option.rawName || option.value,
      }));

      const response = await api.post(
        '/ai/ebay-listing/generate',
        createAIFormData({
          currentData: aiBaseData,
          requiredFields: [],
          specificsOverride: aiBaseData.ebay_specifics || {},
          categoryLocked: false,
          candidateCategoriesOverride: candidateCategories,
          extraInstructionOverride:
            'Choose the single best eBay category from the provided candidateCategories only. Do not invent a category ID. Return the selected ebay_category_id and category label/path.',
        }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const pickedAI = response.data?.listing || response.data || {};

      const pickedCategoryId =
        pickedAI.ebay_category_id ||
        pickedAI.category_selection?.selected_category_id ||
        pickedAI.category_id ||
        pickedAI.categoryId ||
        '';

      if (!pickedCategoryId) {
        return {
          needsManualPick: true,
          term: bestTerm,
          options: bestOptions,
          reason: 'AI did not return a selected eBay category ID.',
        };
      }

      const matchedOption =
        bestOptions.find((option) => String(option.value) === String(pickedCategoryId)) ||
        bestOptions.find(
          (option) =>
            normalizeComparable(option.label) === normalizeComparable(pickedAI.ebay_category_label)
        ) ||
        bestOptions.find(
          (option) =>
            normalizeComparable(option.path) === normalizeComparable(pickedAI.category_path)
        );

      if (!matchedOption) {
        return {
          needsManualPick: true,
          term: bestTerm,
          options: bestOptions,
          reason: `AI selected category ${pickedCategoryId}, but it was not found in the candidate list.`,
        };
      }

      setAiAutoCategoryNote(
        `AI selected eBay category "${matchedOption.path || matchedOption.label}" from ${bestOptions.length} real eBay matches.`
      );

      setAiResult((prev) => ({
        ...(prev || {}),
        category_auto_pick: pickedAI,
      }));

      return matchedOption;
    } catch (err) {
      console.error('[AddProduct] AI category auto-pick failed:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });

      return {
        needsManualPick: true,
        term: bestTerm,
        options: bestOptions,
        reason:
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          'AI category auto-pick failed.',
      };
    }
  };

  const handleGenerateWithAI = async () => {
    setError(null);
    setSuccess(null);
    setAiWarning(null);
    setAiResult(null);
    setEbayDraftResult(null);
    setAiAutoCategoryNote(null);

    if (!formData.images.length) {
      setError('Upload at least one image before generating with AI.');
      return;
    }

    if (formData.images.length > MAX_IMAGES) {
      setError(`You can upload a maximum of ${MAX_IMAGES} images.`);
      return;
    }

    const token = localStorage.getItem('token');

    if (!token) {
      setError('Not authenticated. Please log in.');
      navigate('/login');
      return;
    }

    setIsGeneratingAI(true);
    setAiStepMessage('Reading photos and generating the first eBay listing draft...');

    try {
      const response = await api.post('/ai/ebay-listing/generate', createAIFormData(), {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const ai = response.data?.listing || response.data || {};
      const aiSpecifics = normalizeSpecificsObject(ai.item_specifics || ai.ebay_specifics || {});
      const commonFields = applyCommonFieldsFromAI(ai, aiSpecifics, formData);

      const mergedSpecifics = {
        ...formData.ebay_specifics,
        ...aiSpecifics,
      };

      if (commonFields.brand) mergedSpecifics.Brand = commonFields.brand;
      if (commonFields.model) mergedSpecifics.Model = commonFields.model;
      if (commonFields.upc) mergedSpecifics.UPC = commonFields.upc;
      if (commonFields.ean) mergedSpecifics.EAN = commonFields.ean;
      if (commonFields.isbn) mergedSpecifics.ISBN = commonFields.isbn;
      if (commonFields.gtin) mergedSpecifics.GTIN = commonFields.gtin;
      if (commonFields.mpn) mergedSpecifics.MPN = commonFields.mpn;

      const aiBaseData = {
        ...formData,
        listedOn: formData.listedOn.includes('eBay')
          ? formData.listedOn
          : [...formData.listedOn, 'eBay'],
        name: ai.title || ai.name || formData.name,
        description: ai.description || formData.description,
        price: getBestPriceFromAIResponse(ai, formData.price),
        stock: formData.stock || '1',
        condition: ai.condition || formData.condition,
        condition_notes: ai.condition_notes || ai.conditionNotes || formData.condition_notes,
        brand: commonFields.brand || formData.brand,
        model: commonFields.model || formData.model,
        upc: commonFields.upc || formData.upc,
        ean: commonFields.ean || formData.ean,
        isbn: commonFields.isbn || formData.isbn,
        gtin: commonFields.gtin || formData.gtin,
        mpn: commonFields.mpn || formData.mpn,
        ebay_category_search_terms: normalizeArray(ai.category_search_terms),
        ebay_required_specifics_missing: normalizeArray(ai.required_specifics_missing),
        ebay_specifics: mergedSpecifics,
      };

      const aiCategoryId =
        ai.ebay_category_id ||
        ai.category_selection?.selected_category_id ||
        ai.category_id ||
        ai.categoryId ||
        '';

      const aiCategoryLabel =
        ai.ebay_category_label ||
        ai.category_selection?.selected_category_label ||
        ai.category_label ||
        ai.category_name ||
        ai.categoryName ||
        '';

      const aiCategoryPath =
        ai.category_path ||
        ai.category_selection?.selected_category_path ||
        ai.categoryPath ||
        ai.ebay_category_label ||
        aiCategoryLabel ||
        '';

      let categoryOption = null;
      let categoryManualPickResult = null;

      if (aiCategoryId) {
        categoryOption = {
          value: aiCategoryId,
          label: aiCategoryPath || aiCategoryLabel
            ? `${aiCategoryPath || aiCategoryLabel} (ID: ${aiCategoryId})`
            : `ID: ${aiCategoryId}`,
          rawName: aiCategoryLabel,
          path: aiCategoryPath,
        };
      } else {
        categoryManualPickResult = await maybeAutoSelectCategoryFromAISearchTerms(ai, aiBaseData);

        if (categoryManualPickResult && !categoryManualPickResult.needsManualPick) {
          categoryOption = categoryManualPickResult;
        }
      }

      const finalCategoryId = categoryOption?.value || aiCategoryId || '';
      const finalCategoryLabel = categoryOption?.rawName || aiCategoryLabel || '';
      const finalCategoryPath = categoryOption?.path || aiCategoryPath || '';

      const nextDataBase = {
        ...aiBaseData,
        ebay_category_id: finalCategoryId,
        ebay_category_label: finalCategoryLabel,
        ebay_category_path: finalCategoryPath,
        ebay_category_confidence:
          ai.category_confidence !== null && ai.category_confidence !== undefined
            ? String(ai.category_confidence)
            : formData.ebay_category_confidence,
      };

      setFormData(nextDataBase);
      setAiResult(ai);

      if (categoryOption) {
        setSelectedEbayCategory(categoryOption);
      }

      let loadedSpecifics = [];

      if (finalCategoryId) {
        setAiStepMessage('Loading exact eBay item specifics for the selected category...');
        loadedSpecifics = await fetchCategorySpecifics(finalCategoryId, mergedSpecifics, nextDataBase);

        if (categoryOption && loadedSpecifics.length) {
          await refillSpecificsWithAI({
            categoryOption,
            fields: loadedSpecifics,
            automatic: true,
            currentDataOverride: nextDataBase,
          });
        }
      }

      const warnings = [];
      const aiWarningMessage = buildAIWarningMessage(ai);

      if (aiWarningMessage) warnings.push(aiWarningMessage);

      if (categoryManualPickResult?.needsManualPick) {
        warnings.push(
          `AI found multiple eBay categories for "${categoryManualPickResult.term}" but could not safely choose one. ${categoryManualPickResult.reason || 'Please review the category.'}`
        );
      }

      if (!finalCategoryId) {
        warnings.push(
          'AI suggested category information but no exact eBay category ID was selected. Pick the eBay category before submitting.'
        );
      }

      if (warnings.length) {
        setAiWarning(warnings.join(' '));
      } else {
        setSuccess('AI filled the listing draft, picked the eBay category, and filled specifics. Review before submitting.');
      }
    } catch (err) {
      console.error('Error generating AI listing:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });

      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
        localStorage.removeItem('token');
        setTimeout(() => navigate('/login'), 1500);
      } else if (err.response?.status === 413) {
        setError('Images are too large. Try fewer images or smaller image files.');
      } else if (err.response?.status === 429) {
        setError('AI or API rate limit hit. Try again in a minute.');
      } else {
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            'Failed to generate listing with AI.'
        );
      }
    } finally {
      setIsGeneratingAI(false);
      setAiStepMessage('');
    }
  };

  const resetForm = () => {
    imagePreviews.forEach((preview) => {
      if (preview.url) URL.revokeObjectURL(preview.url);
    });

    setFormData(emptyFormData);
    setImagePreviews([]);
    setSpecificFields([]);
    setSelectedEbayCategory(null);
    setAiResult(null);
    setAiWarning(null);
    setAiAutoCategoryNote(null);
    setEbayDraftResult(null);
    setAiStepMessage('');
  };

  const createEbayDraftForSavedProduct = async (sku) => {
    if (!sku) {
      return {
        success: false,
        error: 'Missing SKU for eBay draft creation.',
      };
    }

    setIsCreatingEbayDraft(true);

    try {
      const response = await api.post('/ebay/drafts/create', { sku });

      const result = response.data || {};
      setEbayDraftResult(result);

      if (result.success) return result;

      return {
        success: false,
        error: result.error || 'eBay draft creation failed.',
      };
    } catch (err) {
      console.error('Error creating eBay draft:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });

      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to create eBay draft.';

      const result = {
        success: false,
        error: message,
      };

      setEbayDraftResult(result);
      return result;
    } finally {
      setIsCreatingEbayDraft(false);
    }
  };

  const getMissingRequiredSpecificNames = (submitData, fields = specificFields) => {
    if (!submitData.listedOn.includes('eBay')) return [];
    if (!fields || fields.length === 0) return [];

    const normalizedSpecifics = normalizeSpecificsObject(submitData.ebay_specifics || {});

    return fields
      .filter((field) => {
        if (!field.required) return false;

        const exactValue = submitData.ebay_specifics?.[field.name];
        const normalizedName = normalizeSpecificKey(field.name);
        const normalizedValue = normalizedSpecifics[normalizedName];

        return !cleanText(exactValue) && !cleanText(normalizedValue);
      })
      .map((field) => field.name);
  };

  const isDuplicateSkuError = (err) => {
    const message = [
      err.response?.data?.details,
      err.response?.data?.error,
      err.response?.data?.message,
      err.message,
    ]
      .filter(Boolean)
      .join(' ');

    return message.includes('Duplicate entry') && message.includes('products.sku');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError(null);
    setSuccess(null);
    setEbayDraftResult(null);
    setIsSubmittingProduct(true);

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Not authenticated. Please log in.');
        navigate('/login');
        return;
      }

      const autoSku = await generateSkuIfNeeded(formData.sku);
      const bestName = cleanText(formData.name) || getBestAIName();
      const bestPrice =
      cleanText(formData.price) ||
      getBestAIPrice() ||
      getBestPriceFromAIResponse(aiResult, '');
      const bestStock = cleanText(formData.stock) || '1';

      let submitData = {
        ...formData,
        sku: autoSku,
        name: bestName,
        price: bestPrice,
        stock: bestStock,
      };

      if (submitData.listedOn.includes('eBay')) {
        submitData = {
          ...submitData,
          ebay_specifics: autoFillSpecificsFromKnownData(
            submitData,
            specificFields,
            submitData.ebay_specifics || {}
          ),
        };
      }

      setFormData((prev) => ({
        ...prev,
        sku: autoSku,
        name: bestName,
        price: bestPrice,
        stock: bestStock,
        ebay_specifics: submitData.ebay_specifics,
      }));

      if (!submitData.sku || !submitData.name || !submitData.price || !submitData.stock) {
        setError(
          'Name and Price are still required. Generate with AI first or enter them manually. SKU and stock can be auto-filled.'
        );
        return;
      }

      const price = Number(submitData.price);
      const mapPrice = Number(submitData.map_price) || 0;

      if (Number.isNaN(price) || price <= 0) {
        setError('Price must be a positive number. Generate with AI or enter a price manually.');
        return;
      }

      if (mapPrice > 0 && price < mapPrice) {
        setError(`Price cannot be lower than MAP Price ($${mapPrice.toFixed(2)}).`);
        return;
      }

      if (Number(submitData.stock) < 0) {
        setError('Stock must be a non-negative number.');
        return;
      }

      if (submitData.images.length > MAX_IMAGES) {
        setError(`You can upload a maximum of ${MAX_IMAGES} images.`);
        return;
      }

      if (submitData.listedOn.includes('eBay') && !submitData.ebay_category_id) {
        setError('eBay category is required when listing on eBay.');
        return;
      }

      const missingRequiredSpecificNames = getMissingRequiredSpecificNames(submitData);

      if (missingRequiredSpecificNames.length > 0) {
        setAiWarning(
          `Some eBay required specifics may be missing: ${missingRequiredSpecificNames.join(
            ', '
          )}. I will still save locally and try the eBay draft so eBay can return the exact issue if it rejects it.`
        );
      }

      const data = new FormData();

      data.append('sku', submitData.sku);
      data.append('name', submitData.name);
      data.append('price', submitData.price);
      data.append('map_price', submitData.map_price || '');
      data.append('stock', submitData.stock);
      data.append('description', submitData.description || '');

      if (submitData.images[0]) {
        data.append('image', submitData.images[0]);
      }

      submitData.images.forEach((file) => {
        data.append('images', file);
      });

      data.append('binLocation', submitData.binLocation || '');
      data.append('length', submitData.length || '');
      data.append('width', submitData.width || '');
      data.append('height', submitData.height || '');
      data.append('weight', submitData.weight || '');
      data.append('platform', submitData.listedOn.join(','));

      data.append('condition', submitData.condition || '');
      data.append('condition_notes', submitData.condition_notes || '');
      data.append('brand', submitData.brand || '');
      data.append('model', submitData.model || '');
      data.append('upc', submitData.upc || '');
      data.append('ean', submitData.ean || '');
      data.append('isbn', submitData.isbn || '');
      data.append('gtin', submitData.gtin || '');
      data.append('mpn', submitData.mpn || '');

      if (submitData.ebay_category_id) {
        data.append('ebay_category_id', submitData.ebay_category_id);
        data.append('ebay_category_label', submitData.ebay_category_label || '');
        data.append('ebay_category_path', submitData.ebay_category_path || '');
        data.append('ebay_category_confidence', submitData.ebay_category_confidence || '');
        data.append(
          'ebay_category_search_terms',
          JSON.stringify(submitData.ebay_category_search_terms || [])
        );
        data.append('ebay_specifics', JSON.stringify(submitData.ebay_specifics || {}));
        data.append(
          'ebay_required_specifics_missing',
          JSON.stringify(missingRequiredSpecificNames || [])
        );
      }

      if (aiResult) {
        data.append('ai_listing_json', JSON.stringify(aiResult));
      }

      const productResponse = await api.post('/products', data);
      console.log('[AddProduct] Local product created:', productResponse.data);

      if (submitData.listedOn.includes('eBay')) {
        const draftResult = await createEbayDraftForSavedProduct(submitData.sku);

        if (draftResult.success) {
          setSuccess(
            `Product added locally and eBay draft created. Offer ID: ${
              draftResult.ebay_offer_id || 'created'
            }`
          );

          resetForm();
          setTimeout(() => navigate('/inventory'), 2500);
          return;
        }

        const missingText =
          missingRequiredSpecificNames.length > 0
            ? ` Possible missing specifics: ${missingRequiredSpecificNames.join(', ')}.`
            : '';

        setAiWarning(
          `Product was added locally, but eBay draft creation failed: ${
            draftResult.error || 'Unknown eBay error'
          }.${missingText}`
        );

        setSuccess('Product added locally. Fix the eBay draft issue, then retry draft creation.');
        return;
      }

      setSuccess('Product added successfully!');
      resetForm();

      setTimeout(() => navigate('/inventory'), 2000);
    } catch (err) {
      console.error('Error adding product:', err);

      if (isDuplicateSkuError(err)) {
        const existingSku = cleanText(formData.sku);

        if (existingSku && formData.listedOn.includes('eBay')) {
          setAiWarning(
            `SKU ${existingSku} already exists locally. Retrying eBay draft creation only...`
          );

          const draftResult = await createEbayDraftForSavedProduct(existingSku);

          if (draftResult.success) {
            setSuccess(
              `Product already existed locally and eBay draft was created. Offer ID: ${
                draftResult.ebay_offer_id || 'created'
              }`
            );

            setTimeout(() => navigate('/inventory'), 2500);
            return;
          }

          setError(
            `Product already exists locally, but eBay draft creation failed: ${
              draftResult.error || 'Unknown eBay error'
            }`
          );
          return;
        }

        setError(
          `SKU ${existingSku || ''} already exists. Use a different SKU or open the existing product.`
        );
        return;
      }

      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
        localStorage.removeItem('token');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        setError(
          err.response?.data?.details ||
            err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            'Failed to add product'
        );
      }
    } finally {
      setIsSubmittingProduct(false);
    }
  };

  const renderAIReviewCard = () => {
    if (!aiResult) return null;

    const missingInfo = normalizeArray(aiResult.missing_info);
    const requiredMissing = normalizeArray(aiResult.required_specifics_missing);
    const visibleFlaws = normalizeArray(aiResult.visible_flaws);
    const includedItems = normalizeArray(aiResult.included_items);
    const photoNotes = normalizeArray(aiResult.photo_notes);
    const warnings = normalizeArray(aiResult.warnings);

    return (
      <Card className="mt-3">
        <Card.Body>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            <h6 className="mb-0">AI Review Summary</h6>

            <div className="d-flex flex-wrap gap-2">
              {aiResult.confidence !== undefined && aiResult.confidence !== null && (
                <Badge bg={Number(aiResult.confidence) >= 0.78 ? 'success' : 'warning'}>
                  Confidence {Math.round(Number(aiResult.confidence) * 100)}%
                </Badge>
              )}

              {aiResult.category_confidence !== undefined &&
                aiResult.category_confidence !== null && (
                  <Badge bg={Number(aiResult.category_confidence) >= 0.75 ? 'success' : 'warning'}>
                    Category {Math.round(Number(aiResult.category_confidence) * 100)}%
                  </Badge>
                )}

              {aiResult.listing_quality_score !== undefined &&
                aiResult.listing_quality_score !== null && (
                  <Badge bg={Number(aiResult.listing_quality_score) >= 0.75 ? 'success' : 'warning'}>
                    Quality {Math.round(Number(aiResult.listing_quality_score) * 100)}%
                  </Badge>
                )}

              {aiResult.category_refill_done && <Badge bg="success">Specifics Filled</Badge>}
              {aiResult.needs_review && <Badge bg="danger">Needs Review</Badge>}
            </div>
          </div>

          {aiResult.reason && <p className="text-muted mb-2">{aiResult.reason}</p>}

          {(missingInfo.length > 0 ||
            requiredMissing.length > 0 ||
            warnings.length > 0 ||
            visibleFlaws.length > 0 ||
            includedItems.length > 0 ||
            photoNotes.length > 0) && (
            <ListGroup variant="flush">
              {requiredMissing.length > 0 && (
                <ListGroup.Item>
                  <strong>Missing eBay specifics:</strong> {requiredMissing.join(', ')}
                </ListGroup.Item>
              )}

              {missingInfo.length > 0 && (
                <ListGroup.Item>
                  <strong>Missing info:</strong> {missingInfo.join(', ')}
                </ListGroup.Item>
              )}

              {warnings.length > 0 && (
                <ListGroup.Item>
                  <strong>Warnings:</strong> {warnings.join(' ')}
                </ListGroup.Item>
              )}

              {visibleFlaws.length > 0 && (
                <ListGroup.Item>
                  <strong>Visible flaws:</strong> {visibleFlaws.join(', ')}
                </ListGroup.Item>
              )}

              {includedItems.length > 0 && (
                <ListGroup.Item>
                  <strong>Included items:</strong> {includedItems.join(', ')}
                </ListGroup.Item>
              )}

              {photoNotes.length > 0 && (
                <ListGroup.Item>
                  <strong>Photo notes:</strong> {photoNotes.join(' ')}
                </ListGroup.Item>
              )}
            </ListGroup>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderEbayDraftCard = () => {
    if (!ebayDraftResult) return null;

    return (
      <Alert variant={ebayDraftResult.success ? 'success' : 'warning'} className="mt-3 mb-0">
        {ebayDraftResult.success ? (
          <>
            <strong>eBay draft created.</strong>{' '}
            {ebayDraftResult.ebay_offer_id && <>Offer ID: {ebayDraftResult.ebay_offer_id}</>}
          </>
        ) : (
          <>
            <strong>eBay draft not created.</strong>{' '}
            {ebayDraftResult.error || 'Unknown eBay error.'}
          </>
        )}
      </Alert>
    );
  };

  return (
    <Container className="mt-4 mb-5">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="mb-1">Add New Product</h2>
          <div className="text-muted">
            Upload photos, let AI build the eBay draft, review it, then submit.
          </div>
        </div>

        {formData.images.length > 0 && (
          <Badge bg="secondary">
            {formData.images.length}/{MAX_IMAGES} images
          </Badge>
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
          {success}
        </Alert>
      )}

      {aiWarning && (
        <Alert variant="warning" onClose={() => setAiWarning(null)} dismissible>
          {aiWarning}
        </Alert>
      )}

      {aiAutoCategoryNote && (
        <Alert variant="info" onClose={() => setAiAutoCategoryNote(null)} dismissible>
          {aiAutoCategoryNote}
        </Alert>
      )}

      {aiStepMessage && (
        <Alert variant="secondary">
          <Spinner size="sm" animation="border" className="me-2" />
          {aiStepMessage}
        </Alert>
      )}

      <Form onSubmit={handleSubmit} noValidate>
        <Tabs defaultActiveKey="general" className="mb-4">
          <Tab eventKey="general" title="General">
            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">AI Listing Generator</h5>

                <Form.Group className="mb-3" controlId="images">
                  <Form.Label>Images Max {MAX_IMAGES}</Form.Label>
                  <Form.Control
                    type="file"
                    name="images"
                    onChange={handleFileChange}
                    accept="image/*"
                    multiple
                  />
                  <Form.Text className="text-muted">
                    Use clear photos of the front, back, labels, model numbers, UPC, damage,
                    and included accessories.
                  </Form.Text>
                </Form.Group>

                {imagePreviews.length > 0 && (
                  <Row className="g-3 mb-3">
                    {imagePreviews.map((preview, index) => (
                      <Col key={preview.id} xs={6} md={3} lg={2}>
                        <div className="border rounded p-2 h-100">
                          <div className="position-relative">
                            <img
                              src={preview.url}
                              alt={`Preview ${index + 1}`}
                              style={{
                                width: '100%',
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 6,
                              }}
                            />

                            {index === 0 && (
                              <Badge bg="primary" className="position-absolute top-0 start-0 m-1">
                                Main
                              </Badge>
                            )}
                          </div>

                          <div className="small text-truncate mt-2" title={preview.name}>
                            {preview.name}
                          </div>

                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="w-100 mt-2"
                            type="button"
                            onClick={() => removeImage(index)}
                          >
                            Remove
                          </Button>
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}

                <div className="d-flex flex-wrap gap-2">
                  <Button
                    variant="warning"
                    type="button"
                    onClick={handleGenerateWithAI}
                    disabled={
                      isGeneratingAI ||
                      isRefillingSpecifics ||
                      !formData.images.length ||
                      isCreatingEbayDraft ||
                      isSubmittingProduct
                    }
                  >
                    {isGeneratingAI ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-2" />
                        Building Listing...
                      </>
                    ) : (
                      'Generate Full eBay Listing with AI'
                    )}
                  </Button>

                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => {
                      setAiResult(null);
                      setAiWarning(null);
                      setAiAutoCategoryNote(null);
                      setSuccess(null);
                      setEbayDraftResult(null);
                      setAiStepMessage('');
                    }}
                    disabled={
                      isGeneratingAI ||
                      isRefillingSpecifics ||
                      isCreatingEbayDraft ||
                      isSubmittingProduct
                    }
                  >
                    Clear AI Notes
                  </Button>
                </div>

                {aiResult && (
                  <Alert variant="info" className="mt-3 mb-0">
                    AI draft applied. Review title, condition, price, category, and specifics before submitting.
                  </Alert>
                )}

                {renderAIReviewCard()}
                {renderEbayDraftCard()}
              </Card.Body>
            </Card>

            <Form.Group className="mb-3" controlId="sku">
              <Form.Label>SKU</Form.Label>
              <Form.Control
                type="text"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                placeholder="Leave blank to auto-generate SKU"
              />
              <Form.Text className="text-muted">
                Blank SKU will be auto-generated when you submit.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="name">
              <Form.Label>Name / eBay Title</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter product name or generate with AI"
                maxLength={80}
              />
              <Form.Text className="text-muted">
                eBay titles should stay under 80 characters. Current: {formData.name.length}/80
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="description">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter description"
                rows={5}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="price">
                  <Form.Label>Price ($)</Form.Label>
                  <Form.Control
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="Blank uses AI suggested price if available"
                    step="0.01"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3" controlId="map_price">
                  <Form.Label>MAP Price ($)</Form.Label>
                  <Form.Control
                    type="number"
                    name="map_price"
                    value={formData.map_price}
                    onChange={handleChange}
                    placeholder="Enter Minimum Advertised Price"
                    step="0.01"
                    min="0"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="stock">
              <Form.Label>Stock</Form.Label>
              <Form.Control
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                placeholder="Blank defaults to 1"
              />
            </Form.Group>
          </Tab>

          <Tab eventKey="details" title="Item Details">
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="brand">
                  <Form.Label>Brand</Form.Label>
                  <Form.Control
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    placeholder="Brand"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3" controlId="model">
                  <Form.Label>Model</Form.Label>
                  <Form.Control
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    placeholder="Model"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="upc">
                  <Form.Label>UPC</Form.Label>
                  <Form.Control
                    type="text"
                    name="upc"
                    value={formData.upc}
                    onChange={handleChange}
                    placeholder="UPC"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3" controlId="mpn">
                  <Form.Label>MPN</Form.Label>
                  <Form.Control
                    type="text"
                    name="mpn"
                    value={formData.mpn}
                    onChange={handleChange}
                    placeholder="MPN"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3" controlId="ean">
                  <Form.Label>EAN</Form.Label>
                  <Form.Control
                    type="text"
                    name="ean"
                    value={formData.ean}
                    onChange={handleChange}
                    placeholder="EAN"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3" controlId="isbn">
                  <Form.Label>ISBN</Form.Label>
                  <Form.Control
                    type="text"
                    name="isbn"
                    value={formData.isbn}
                    onChange={handleChange}
                    placeholder="ISBN"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3" controlId="gtin">
                  <Form.Label>GTIN</Form.Label>
                  <Form.Control
                    type="text"
                    name="gtin"
                    value={formData.gtin}
                    onChange={handleChange}
                    placeholder="GTIN"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="condition">
              <Form.Label>Condition</Form.Label>
              <Form.Select name="condition" value={formData.condition} onChange={handleChange}>
                <option value="">Select condition</option>
                <option value="New">New</option>
                <option value="Open box">Open box</option>
                <option value="Used">Used</option>
                <option value="For parts or not working">For parts or not working</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="condition_notes">
              <Form.Label>Condition Notes</Form.Label>
              <Form.Control
                as="textarea"
                name="condition_notes"
                value={formData.condition_notes}
                onChange={handleChange}
                placeholder="Describe cosmetic wear, testing, missing pieces, damage, etc."
                rows={3}
              />
            </Form.Group>
          </Tab>

          <Tab eventKey="dimensions" title="Dimensions">
            <Form.Group className="mb-3" controlId="binLocation">
              <Form.Label>Bin Location</Form.Label>
              <Form.Control
                type="text"
                name="binLocation"
                value={formData.binLocation}
                onChange={handleChange}
                placeholder="Enter bin location"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3" controlId="length">
                  <Form.Label>Length (in)</Form.Label>
                  <Form.Control
                    type="number"
                    name="length"
                    value={formData.length}
                    onChange={handleChange}
                    placeholder="Enter length"
                    step="0.01"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3" controlId="width">
                  <Form.Label>Width (in)</Form.Label>
                  <Form.Control
                    type="number"
                    name="width"
                    value={formData.width}
                    onChange={handleChange}
                    placeholder="Enter width"
                    step="0.01"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3" controlId="height">
                  <Form.Label>Height (in)</Form.Label>
                  <Form.Control
                    type="number"
                    name="height"
                    value={formData.height}
                    onChange={handleChange}
                    placeholder="Enter height"
                    step="0.01"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="weight">
              <Form.Label>Weight (lbs)</Form.Label>
              <Form.Control
                type="number"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                placeholder="Enter weight"
                step="0.01"
              />
            </Form.Group>
          </Tab>

          <Tab eventKey="platforms" title="Platforms">
            <h5 className="mb-3">Select Platforms to List On</h5>

            <div className="d-flex gap-2 mb-4">
              {['Shopify', 'eBay', 'Walmart'].map((platform) => (
                <Button
                  key={platform}
                  type="button"
                  variant={formData.listedOn.includes(platform) ? 'success' : 'outline-success'}
                  onClick={() => handleListOnPlatform(platform)}
                >
                  {platform}
                </Button>
              ))}
            </div>

            {formData.listedOn.includes('eBay') && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>
                    eBay Category {isLoadingCategories && <span>(Loading...)</span>}
                  </Form.Label>

                  <AsyncSelect
                    cacheOptions
                    loadOptions={loadCategories}
                    onChange={handleCategoryChange}
                    placeholder="AI will choose this, or search manually..."
                    isClearable
                    isLoading={isLoadingCategories}
                    value={selectedEbayCategory}
                  />

                  {formData.ebay_category_id && (
                    <Form.Text className="text-muted d-block">
                      Selected category ID: {formData.ebay_category_id}
                    </Form.Text>
                  )}

                  {formData.ebay_category_path && (
                    <Form.Text className="text-muted d-block">
                      Category path: {formData.ebay_category_path}
                    </Form.Text>
                  )}

                  {formData.ebay_category_confidence && (
                    <Form.Text className="text-muted d-block">
                      AI category confidence:{' '}
                      {Math.round(Number(formData.ebay_category_confidence) * 100)}%
                    </Form.Text>
                  )}

                  {formData.ebay_category_search_terms.length > 0 && (
                    <Form.Text className="text-muted d-block">
                      AI category search terms: {formData.ebay_category_search_terms.join(', ')}
                    </Form.Text>
                  )}
                </Form.Group>

                {formData.ebay_category_id && (
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <Button
                      variant="outline-warning"
                      type="button"
                      onClick={() => refillSpecificsWithAI({ automatic: false })}
                      disabled={
                        !formData.images.length ||
                        !specificFields.length ||
                        isRefillingSpecifics ||
                        isGeneratingAI ||
                        isCreatingEbayDraft ||
                        isSubmittingProduct
                      }
                    >
                      {isRefillingSpecifics ? (
                        <>
                          <Spinner size="sm" animation="border" className="me-2" />
                          Filling Specifics...
                        </>
                      ) : (
                        'Refill eBay Specifics with AI'
                      )}
                    </Button>

                    {specificFields.length > 0 && (
                      <Button
                        variant="outline-info"
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            ebay_specifics: autoFillSpecificsFromKnownData(
                              prev,
                              specificFields,
                              prev.ebay_specifics || {}
                            ),
                          }));
                        }}
                        disabled={
                          isRefillingSpecifics ||
                          isGeneratingAI ||
                          isCreatingEbayDraft ||
                          isSubmittingProduct
                        }
                      >
                        Auto-Fill Known Specifics
                      </Button>
                    )}

                    {specificFields.length > 0 && (
                      <Badge bg="secondary" className="align-self-center">
                        {specificFields.length} category fields loaded
                      </Badge>
                    )}
                  </div>
                )}

                {specificFields.length > 0 ? (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      eBay Item Specifics {isLoadingSpecifics && <span>(Loading...)</span>}
                    </Form.Label>

                    {specificFields.map((field) => {
                      const currentValue =
                        formData.ebay_specifics[field.name] ||
                        formData.ebay_specifics[normalizeSpecificKey(field.name)] ||
                        findSpecificValue(formData.ebay_specifics, [field.name]) ||
                        '';

                      const allowedValues = getAllowedValuesForSpecificField(field);

                      return (
                        <div key={field.name} className="mb-3">
                          <Form.Label>
                            {field.name}{' '}
                            {field.required && (
                              <Badge bg="danger" className="ms-1">
                                Required
                              </Badge>
                            )}
                          </Form.Label>

                          {allowedValues.length > 0 && !field.isFreeText ? (
                            <Form.Select
                              value={currentValue}
                              onChange={handleSpecificChange(field.name)}
                            >
                              <option value="">Select {field.name}</option>
                              {allowedValues.map((value) => (
                                <option key={value} value={value}>
                                  {value}
                                </option>
                              ))}
                            </Form.Select>
                          ) : (
                            <Form.Control
                              type="text"
                              value={currentValue}
                              onChange={handleSpecificChange(field.name)}
                              placeholder={field.required ? `${field.name} required` : field.name}
                            />
                          )}

                          {field.helpText && (
                            <Form.Text className="text-muted">{field.helpText}</Form.Text>
                          )}
                        </div>
                      );
                    })}
                  </Form.Group>
                ) : (
                  formData.ebay_category_id && (
                    <Alert variant="secondary">
                      No eBay specifics loaded yet, or this category does not require them.
                    </Alert>
                  )
                )}

                {Object.keys(formData.ebay_specifics || {}).length > 0 && (
                  <Card className="mb-3">
                    <Card.Body>
                      <h6>Current eBay Specifics</h6>
                      <Row>
                        {Object.entries(formData.ebay_specifics).map(([key, value]) => (
                          <Col md={6} key={key} className="mb-2">
                            <strong>{key}:</strong> {String(value)}
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  </Card>
                )}
              </>
            )}
          </Tab>
        </Tabs>

        <div className="d-flex gap-2 mb-5">
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmittingProduct || isCreatingEbayDraft || isGeneratingAI || isRefillingSpecifics}
          >
            {isSubmittingProduct || isCreatingEbayDraft ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Saving...
              </>
            ) : (
              'Add Product + Create eBay Draft'
            )}
          </Button>

          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/inventory')}
            disabled={isSubmittingProduct || isCreatingEbayDraft}
          >
            Cancel
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default AddProduct;